/**
 * Custom hooks for scenario planner functionality
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, useCallback } from 'react';
import { SCENARIO_PLANNER_QUERY_KEYS } from '../../routes/scenario-planner/components/scenario-planner.constants';
import {
  getScenarioPlanners,
  postScenarioPlanner,
  getScenarioPlanner,
  putScenarioPlanner,
  postScenarioPlannerClone,
} from '../../services/scenario-planner.service';
import { FilterSelectedValue } from '../filters/interfaces/filter-selected-value.interface';
import {
  ScenarioPlannerResponse,
  ScenarioPlanner,
  ScenarioPlannerListResponse,
} from './scenario-planner.interface';

export const MAX_SCENARIOS_FOR_COMPARISON = 5;
export const MIN_SCENARIOS_FOR_COMPARISON = 2;

/**
 * Hook to create a new scenario planner
 * @returns Mutation object with create function and status
 */
export const useCreateScenarioPlanner = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ScenarioPlannerResponse,
    Error,
    {
      name: string;
      description: string;
      filterSelection: FilterSelectedValue[];
    }
  >({
    mutationFn: ({ name, description, filterSelection }) =>
      postScenarioPlanner(name, description, filterSelection),
    onSuccess: () => {
      // Invalidate the scenario planners query to refetch the list
      queryClient.invalidateQueries({
        queryKey: [SCENARIO_PLANNER_QUERY_KEYS.SCENARIO_PLANNERS],
      });
    },
  });
};

/**
 * Hook to get a specific scenario planner by ID
 * @param id ID of the scenario planner to fetch
 * @param enabled Whether to enable the query (default: true if ID is provided)
 * @returns Query result with scenario planner data
 */
export const useScenarioPlanner = (
  id: string | null | undefined,
  enabled = !!id,
) => {
  return useQuery<ScenarioPlannerResponse>({
    queryKey: [SCENARIO_PLANNER_QUERY_KEYS.SCENARIO_PLANNER, id],
    queryFn: async () => {
      if (!id) throw new Error('No scenario planner ID provided');
      return await getScenarioPlanner<ScenarioPlannerResponse>(id);
    },
    enabled,
    throwOnError: true,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to update an existing scenario planner
 * @returns Mutation object with update function and status
 */
export const useUpdateScenarioPlanner = () => {
  const queryClient = useQueryClient();
  return useMutation<
    ScenarioPlannerResponse,
    Error,
    {
      id: string;
      name: string;
      description: string;
      filterSelection: FilterSelectedValue[];
    }
  >({
    mutationFn: ({ id, name, description, filterSelection }) =>
      putScenarioPlanner(id, name, description, filterSelection),
    onSuccess: (data, variables) => {
      // Invalidate the specific scenario planner query
      queryClient.invalidateQueries({
        queryKey: [SCENARIO_PLANNER_QUERY_KEYS.SCENARIO_PLANNER, variables.id],
      });
      // Invalidate the list of scenario planners
      queryClient.invalidateQueries({
        queryKey: [SCENARIO_PLANNER_QUERY_KEYS.SCENARIO_PLANNERS],
      });
    },
  });
};

/**
 * Hook to get all scenario planners for a user
 * @param enabled Whether to enable the query (default: true)
 * @param throwOnError Whether to throw on error (default: true)
 * @returns Query result with scenario planners data
 */
export const useScenarioPlanners = (enabled = true, throwOnError = true) => {
  return useQuery<ScenarioPlanner[]>({
    queryKey: [SCENARIO_PLANNER_QUERY_KEYS.SCENARIO_PLANNERS],
    queryFn: async () => {
      const response = await getScenarioPlanners<ScenarioPlannerListResponse>();
      return response.scenarioPlanners;
    },
    enabled,
    throwOnError,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to map scenario planners data to table row format
 * @param scenarioPlanners Raw scenario planners data
 * @returns Formatted data for table display
 */
export const useScenarioPlannerTableData = (
  scenarioPlanners: ScenarioPlanner[] | undefined,
) => {
  if (!scenarioPlanners) return [];

  return scenarioPlanners.map((scenario) => ({
    id: scenario.id,
    name: scenario.name,
    info: scenario.description || 'No description',
    dateModified: formatDate(scenario.updatedTime || scenario.createdTime),
    comparisonId: scenario.comparisonId,
    // Default impact tags and colors (would come from API in a real implementation)
    impactTags: ['Placeholder'],
    impactColors: [['#ECFDF3', '#ABEFC6', '#027A48']],
  }));
};

/**
 * Helper function to format date strings
 * @param dateStr Date string
 * @returns Formatted date string
 */
const formatDate = (dateStr: string): string => {
  if (!dateStr) return 'N/A';

  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    return dateStr;
  }
};

/**
 * Hook to clone an existing scenario planner
 * @param navigateToCloned Whether to automatically navigate to the cloned scenario on success
 * @returns Mutation object with clone function and status
 */
export const useCloneScenarioPlanner = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ScenarioPlannerResponse,
    Error,
    {
      id: string;
      name: string;
      shareKey?: string; // The user can clone a scenario of another user only if a shareKey is provided.
    }
  >({
    mutationFn: ({ id, name, shareKey }) =>
      postScenarioPlannerClone(id, name, shareKey),
    onSuccess: () => {
      // Invalidate the scenario planners query to refetch the list
      queryClient.invalidateQueries({
        queryKey: [SCENARIO_PLANNER_QUERY_KEYS.SCENARIO_PLANNERS],
      });
    },
  });
};

// Interface for comparison details
interface ComparisonDetails {
  selectedScenarioIds: string[];
  selectedScenarios: ScenarioPlanner[];
  comparisonIds: string[];
  uniqueComparisonIds: string[];
  isEligibleCount: boolean;
  hasValidComparisonIds: boolean;
}

// Return type for the hook
interface ScenarioComparisonEligibility {
  isEligibleForComparison: boolean;
  comparisonDetails: ComparisonDetails;
  maxScenariosAllowed: number;
  minScenariosRequired: number;
  eligibleComparisonId: string | null;
}

/**
 * Hook to determine if selected scenarios are eligible for comparison
 * based on their KC impact metrics
 */
export const useScenarioComparisonEligibility = (
  selectedScenarioIds: string[],
  scenarioPlanners: ScenarioPlanner[],
): ScenarioComparisonEligibility => {
  // Get the selected scenario objects
  const selectedScenarios = useMemo(() => {
    if (!scenarioPlanners || selectedScenarioIds.length === 0) return [];

    return scenarioPlanners.filter((scenario) =>
      selectedScenarioIds.includes(scenario.id),
    );
  }, [selectedScenarioIds, scenarioPlanners]);

  // Extract comparisonIds from selected scenarios
  const comparisonIds = useMemo(() => {
    return selectedScenarios
      .map((scenario) => scenario.comparisonId)
      .filter((id) => id != null && id !== undefined && id !== ''); // Filter out null/undefined/empty values
  }, [selectedScenarios]);

  // Get unique comparisonIds
  const uniqueComparisonIds = useMemo(() => {
    return Array.from(new Set(comparisonIds));
  }, [comparisonIds]);

  // Check if the number of selected scenarios is within valid range
  const isEligibleCount = useMemo(() => {
    return (
      selectedScenarioIds.length >= MIN_SCENARIOS_FOR_COMPARISON &&
      selectedScenarioIds.length <= MAX_SCENARIOS_FOR_COMPARISON
    );
  }, [selectedScenarioIds.length]);

  // Check if all selected scenarios have valid comparisonIds
  const hasValidComparisonIds = useMemo(() => {
    // All selected scenarios must have comparisonId
    if (comparisonIds.length !== selectedScenarios.length) return false;

    // All comparisonIds must be the same (only one unique comparisonId)
    return uniqueComparisonIds.length === 1;
  }, [
    comparisonIds.length,
    selectedScenarios.length,
    uniqueComparisonIds.length,
  ]);

  // Determine if scenarios are eligible for comparison
  const isEligibleForComparison = useMemo(() => {
    return isEligibleCount && hasValidComparisonIds;
  }, [isEligibleCount, hasValidComparisonIds]);

  // Get detailed comparison information
  const comparisonDetails = useMemo((): ComparisonDetails => {
    return {
      selectedScenarioIds,
      selectedScenarios,
      comparisonIds,
      uniqueComparisonIds,
      isEligibleCount,
      hasValidComparisonIds,
    };
  }, [
    selectedScenarioIds,
    selectedScenarios,
    comparisonIds,
    uniqueComparisonIds,
    isEligibleCount,
    hasValidComparisonIds,
  ]);

  // Get the eligible comparisonId when all selected scenarios have the same comparisonId
  const eligibleComparisonId = useMemo(() => {
    // Return the comparisonId only if all selected scenarios have the same one
    if (hasValidComparisonIds && uniqueComparisonIds.length === 1) {
      return uniqueComparisonIds[0];
    }
    return null;
  }, [hasValidComparisonIds, uniqueComparisonIds]);

  return {
    isEligibleForComparison,
    comparisonDetails,
    maxScenariosAllowed: MAX_SCENARIOS_FOR_COMPARISON,
    minScenariosRequired: MIN_SCENARIOS_FOR_COMPARISON,
    eligibleComparisonId,
  };
};

/**
 * Utility function to validate and sanitize elasticity input values
 */
export const validateElasticityValue = (value: string): number | undefined => {
  if (!value || value.trim() === '') {
    return undefined; // Return undefined instead of null for empty values
  }

  const numericValue = parseFloat(value.trim());
  if (isNaN(numericValue)) {
    return undefined; // Return undefined for invalid values
  }

  return numericValue;
};

/**
 * Utility function to format elasticity values for display
 */
export const formatElasticityValue = (
  value: number | null | undefined,
  decimalPlaces: number = 3,
): string => {
  if (value === null || value === undefined) {
    return '0';
  }

  if (typeof value === 'number' && !isNaN(value)) {
    return value.toFixed(decimalPlaces);
  }

  return '0';
};

/**
 * Hook to track field edit state and provide helper functions
 */
export const useFieldEditTracking = <T extends Record<string, any>>(
  initialValues: T,
  isOverwrittenFlags: Record<string, boolean> = {},
) => {
  type FieldState = {
    currentValue: string;
    originalValue: string;
    isEdited: boolean;
    isOverwritten: boolean;
  };

  const [fieldStates, setFieldStates] = useState<Record<string, FieldState>>(
    () => {
      const states: Record<string, FieldState> = {};
      Object.keys(initialValues).forEach((key) => {
        const value = initialValues[key]?.toString() || '0';
        const isOverwritten =
          isOverwrittenFlags[`${key}IsOverwritten`] || false;
        states[key] = {
          currentValue: value,
          originalValue: value,
          isEdited: false,
          isOverwritten,
        };
      });
      return states;
    },
  );

  const updateField = useCallback((fieldId: string, newValue: string) => {
    setFieldStates((prev) => {
      const currentField = prev[fieldId];
      if (!currentField) return prev;

      return {
        ...prev,
        [fieldId]: {
          ...currentField,
          currentValue: newValue,
          isEdited: newValue !== currentField.originalValue,
        },
      };
    });
  }, []);

  const restoreField = useCallback((fieldId: string) => {
    setFieldStates((prev) => {
      const currentField = prev[fieldId];
      if (!currentField) return prev;

      return {
        ...prev,
        [fieldId]: {
          ...currentField,
          currentValue: currentField.originalValue,
          isEdited: false,
        },
      };
    });
  }, []);

  const restoreAllFields = useCallback(() => {
    setFieldStates((prev) => {
      const newStates: Record<string, FieldState> = {};
      Object.keys(prev).forEach((key) => {
        newStates[key] = {
          ...prev[key],
          currentValue: prev[key].originalValue,
          isEdited: false,
        };
      });
      return newStates;
    });
  }, []);

  const getEditedFields = useCallback(() => {
    return Object.entries(fieldStates)
      .filter(([, state]) => state.isEdited)
      .reduce(
        (acc, [key, state]) => {
          // Use undefined for empty values instead of 'null' string
          if (state.currentValue.trim() === '') {
            acc[key] = undefined;
          } else {
            const numericValue = parseFloat(state.currentValue);
            acc[key] = isNaN(numericValue) ? undefined : numericValue;
          }
          return acc;
        },
        {} as Record<string, number | undefined>,
      );
  }, [fieldStates]);

  const hasAnyEdits = useMemo(() => {
    return Object.values(fieldStates).some((state) => state.isEdited);
  }, [fieldStates]);

  return {
    fieldStates,
    updateField,
    restoreField,
    restoreAllFields,
    getEditedFields,
    hasAnyEdits,
  };
};
